/**
 * BUNDLE BUY BUTTONS
 * ---------------------------------------------------------------------------------------------------------------
 * Companion script for sections/bundle-builder.liquid.
 *
 * Each product in the bundle already gets a fully working, independent size/variant picker for free, because the
 * Liquid template renders the theme's own `variant-picker` snippet once per product (the exact same snippet the
 * main product page uses), each bound to its own tiny local <form>. This script's only job is to:
 *
 *   1. Track which variant is currently selected in each of those local forms.
 *   2. Enable/disable the single "Add bundle to cart" button and show a running total price.
 *   3. On click, add every selected variant to the cart in ONE request to the Cart AJAX API
 *      (POST /cart/add.js with an "items" array), instead of Shopify's default single-item form submit.
 *   4. Refresh the cart drawer / cart count and open the drawer, reusing the theme's own public hooks
 *      ("cart:refresh" event + the <cart-drawer> element's show() method) rather than re-implementing them.
 *
 * No files from the theme's own build (theme.js, sections.js, ...) are modified by this script.
 */

class BundleBuyButtons extends HTMLElement {
  connectedCallback() {
    this.formIds = (this.getAttribute('forms') || '').split(',').filter(Boolean);
    this.moneyFormat = this.getAttribute('money-format') || '${{amount}}';
    this.button = this.querySelector('button');
    this.totalPriceElement = this.closest('.bundle-builder')?.querySelector('[data-bundle-total-price]');

    this.variantsByForm = this._readInitialVariants();

    this._onVariantChange = this._onVariantChange.bind(this);
    this.formIds.forEach((formId) => {
      const form = document.forms[formId];
      if (!form) {
        console.warn(`bundle-buy-buttons: no form found with id "${formId}"`);
        return;
      }
      form.addEventListener('variant:change', (event) => this._onVariantChange(formId, event));
    });

    this.button?.addEventListener('click', (event) => this._onAddToCart(event));

    this._render();
  }

  /**
   * The variant-picker web component only dispatches "variant:change" when the shopper actually changes a
   * selection — not for the variant that is already selected on page load. So the initial state (id / price /
   * availability of the variant selected server-side) is seeded from a small JSON blob the Liquid template
   * writes out, instead of waiting for an event that may never fire.
   */
  _readInitialVariants() {
    const script = this.closest('.bundle-builder')?.querySelector('[data-bundle-initial-variants]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('bundle-buy-buttons: could not parse initial variants JSON', error);
      return {};
    }
  }

  _onVariantChange(formId, event) {
    this.variantsByForm[formId] = event.detail.variant
      ? { id: event.detail.variant.id, price: event.detail.variant.price, available: event.detail.variant.available }
      : null;
    this._render();
  }

  _render() {
    if (!this.button) return;

    const variants = this.formIds.map((formId) => this.variantsByForm[formId]);
    const allSelected = variants.every(Boolean);
    const allAvailable = allSelected && variants.every((variant) => variant.available);

    this.button.disabled = !allAvailable;

    const label = this.button.getAttribute('is') === 'custom-button' ? this.button.firstElementChild : this.button;
    if (label) {
      label.textContent = !allSelected
        ? this.getAttribute('unavailable-label') || 'Select all options'
        : !allAvailable
        ? this.getAttribute('sold-out-label') || 'Sold out'
        : this.getAttribute('add-to-cart-label') || 'Add bundle to cart';
    }

    if (this.totalPriceElement) {
      this.totalPriceElement.textContent = allSelected
        ? this._formatMoney(variants.reduce((total, variant) => total + variant.price, 0))
        : '';
    }
  }

  _formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  async _onAddToCart(event) {
    event.preventDefault();

    const items = this.formIds.map((formId) => {
      const form = document.forms[formId];
      return {
        id: parseInt(form.elements['id'].value, 10),
        quantity: parseInt(form.elements['quantity']?.value || '1', 10)
      };
    });

    if (items.some((item) => !item.id)) {
      return;
    }

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          // Needed for Shopify to check inventory, same header the theme's own product form uses.
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || 'Could not add the bundle to your cart.');
      }

      // This store's cart type is currently "drawer" (config/settings_data.json > cart_type). If that is ever
      // changed in Settings > Theme > Cart, adjust the branches below to match:
      //   - "page": already handled — redirects straight to the cart page.
      //   - "popover": open the <cart-notification-drawer> element instead of <cart-drawer>, and populate it
      //     with the cart's "variant-added" section HTML (see BuyButtons/CartNotificationDrawer in theme.js for
      //     the pattern this would need to follow).
      if (window.themeVariables?.settings?.cartType === 'page') {
        window.location.href = `${window.Shopify.routes.root}cart`;
        return;
      }

      // Reuses the theme's own "force a complete refresh" hook (also used when a page is restored from the
      // back/forward cache) so the cart drawer content and the header cart count both update themselves.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.querySelector('cart-drawer')?.show();

      this.dispatchEvent(new CustomEvent('bundle:add', { bubbles: true, detail: { items: responseJson } }));
    } catch (error) {
      this._showError(error.message);
    } finally {
      this.button.removeAttribute('aria-busy');
      this._render();
    }
  }

  _showError(message) {
    const banner = document.createElement('div');
    banner.className = 'banner banner--error justify-center';
    banner.setAttribute('role', 'alert');
    banner.style.marginTop = '1rem';
    banner.style.width = '100%';
    banner.textContent = message;
    this.insertAdjacentElement('afterend', banner);
    setTimeout(() => banner.remove(), 5000);
  }
}

if (!window.customElements.get('bundle-buy-buttons')) {
  window.customElements.define('bundle-buy-buttons', BundleBuyButtons);
}